<template>
  <form @submit.prevent="submitRegularHolidayForm" novalidate>
    <div class="row">
      <div class="col-lg-4 col-lg-4 col-sm-12">
        <Datepicker
            v-model="formRegularHolidayData.startDate"
            :required="true"
            :label="$t('holiday.first_day_of_vacation')"
            position="down"
            :validation="vRegularHoliday$.startDate"
            :messages="vRegularHoliday$.startDate.required.$message"/>
      </div>
      <div class="col-lg-4 col-lg-4 col-sm-12">
        <Datepicker
            v-model="formRegularHolidayData.endDate"
            :required="true"
            :label="$t('holiday.last_day_of_vacation')"
            position="down"
            :validation="vRegularHoliday$.endDate"
            :messages="vRegularHoliday$.endDate.required.$message"/>
      </div>
      <div class="col-lg-4 col-lg-4 col-sm-12">
        <SelectVue
            v-model="formRegularHolidayData.year"
            :options="yearDropdown()"
            :is-clear="true"
            :required="true"
            :label="$t('holiday.year_of_vacation_days')"
            :validation="vRegularHoliday$.year"
            :messages="vRegularHoliday$.year.required.$message"/>
      </div>
      <div class="d-flex justify-content-center w-100 mt-3">
        <button class="btn btn-primary" type="submit">{{ $t('general.button.save') }}</button>
      </div>
    </div>
  </form>
</template>

<script setup>
import holidayService from '@/api/Holiday';
import Datepicker from '@/components/picker/Datepicker.vue';
import SelectVue from '@/components/SelectVue.vue';
import {CloseToast, ErrorToast, SuccessToast} from '@/components/VueToast';
import {requiredRule} from '@/shared/validators/validationRules';
import Form from '@/views/App/Course/PrivateCoachingModule/Form.vue';
import useVuelidate from '@vuelidate/core';
import {computed, defineEmits, defineProps, onMounted, ref} from 'vue';
import {useI18n} from 'vue-i18n';

const {t} = useI18n();
const isSubmitting = ref(false);
const emit = defineEmits(['update:modelValue', 'update:submitForm']);

const props = defineProps({
  regularHolidayId: {
    type: Number,
    required: true,
  },
  submitForm: {
    type: Boolean,
    required: true,
    default: false,
  },
});

const formRegularHolidayData = ref({
  startDate: '',
  endDate: '',
  year: '',
});
const formRegularHolidayRules = ref({
  startDate: requiredRule(t),
  endDate: requiredRule(t),
  year: requiredRule(t),
});

const computedRules = computed(() => formRegularHolidayRules.value);
const vRegularHoliday$ = useVuelidate(computedRules, formRegularHolidayData);

async function submitRegularHolidayForm() {
  vRegularHoliday$.value.$touch();
  if (vRegularHoliday$.value.$invalid) {
    return;
  }

  if (isSubmitting.value) return;
  isSubmitting.value = true;

  try {
    if (props.regularHolidayId) {
      await holidayService.putRegularHoliday(formRegularHolidayData.value, props.regularHolidayId);
    } else {
      await holidayService.postRegularHoliday(formRegularHolidayData.value);
    }

    emit('update:modelValue', false);
    emit('update:submitForm', !props.submitForm);
    SuccessToast(t('general.toasts.save_success'));
  } catch (error) {
    await ErrorToast(error);
  } finally {
    isSubmitting.value = false;
  }
}

function yearDropdown() {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;

  const years = [
    {
      label: currentYear - 1,
      value: currentYear - 1,
    },
    {
      label: new Date().getFullYear(),
      value: new Date().getFullYear(),
    },
  ];

  if (currentMonth === 12) {
    years.push({
      label: currentYear + 1,
      value: currentYear + 1,
      currentMonth,
    });
  }

  return years;
}

async function getRegularHolidayData(regularHolidayId) {
  try {
    CloseToast();
    const {data} = await holidayService.getHolidayDetails(+regularHolidayId);
    formRegularHolidayData.value = data.data;
  } catch (error) {
    await ErrorToast(error);
  }
}

onMounted(async () => {
  if (props.regularHolidayId) {
    await getRegularHolidayData(props.regularHolidayId);
  }
});


</script>
<style scoped>

</style>